import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Select, Space, Spin } from 'antd'
import { SelectFromEnumAsyncStyles } from './styles'
import { ISelectAsync, IEnumOption } from './interface'
import fetchAPI from '../../../lib/utils/fetch-api'
import { debounce } from 'lodash'

const { Option } = Select

function SelectFromEnumAsync({ value, schema, onChange }: ISelectAsync) {
  const [localValue, setLocalValue] = useState<any>(value)
  const [options, setOptions] = useState<IEnumOption[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [searchValue, setSearchValue] = useState<string>('')
  const [displayValue, setDisplayValue] = useState<{ value: any; label: string } | null>(null)

  const abortControllerRef = useRef<AbortController | null>(null)
  const debouncedSearchRef = useRef<ReturnType<typeof debounce> | null>(null)
  const valueRef = useRef(value)

  useEffect(() => {
    valueRef.current = value
  }, [value])

  const apiUrl = useMemo(() => schema?.options?.apiUrl, [schema])
  const collectionCode = useMemo(() => schema?.options?.collectionCode, [schema])
  const collectionId = useMemo(() => schema?.options?.collectionId, [schema])
  const field = useMemo(() => schema?.options?.field, [schema])
  const placeholder = useMemo(
    () => schema?.options?.placeholder || 'Начните вводить для поиска...',
    [schema],
  )
  const allowClear = useMemo(() => Boolean(schema?.options?.allowClear), [schema])
  const disabled = useMemo(() => Boolean(schema?.options?.disabled), [schema])

  const searchOptions = useCallback(
    async (search?: string) => {
      if (!apiUrl) return

      abortControllerRef.current?.abort()
      const controller = new AbortController()
      abortControllerRef.current = controller

      setLoading(true)

      try {
        let url = `${apiUrl}?field=${field}`

        if (collectionCode) {
          url += `&collection_code=${collectionCode}`
        } else if (collectionId) {
          url += `&collection_id=${collectionId}`
        }

        if (search && search.length >= 2) {
          url += `&search=${encodeURIComponent(search)}`
        }

        const response = await fetchAPI(url, { signal: controller.signal })

        setOptions(() => {
          const map = new Map<string, IEnumOption>()
          // добавляем все опции из data
          if (response?.data) {
            response.data.forEach((opt: IEnumOption) => map.set(String(opt.value), opt))
          }
          const currentValue = valueRef.current
          return Array.from(map.values()).filter(opt => {
            if (currentValue === null || currentValue === undefined) return true
            return String(opt.value) !== String(currentValue)
          })
        })
      } catch (error: any) {
        if (error.name !== 'AbortError') {
          console.error('Error searching enum options:', error)
          setOptions([])
        }
      } finally {
        setLoading(false)
      }
    },
    [apiUrl, collectionCode, collectionId, field],
  )

  useEffect(() => {
    debouncedSearchRef.current = debounce((search: string) => {
      searchOptions(search)
    }, 300)

    return () => {
      debouncedSearchRef.current?.cancel()
    }
  }, [searchOptions])

  const handleSearch = useCallback((search: string) => {
    setSearchValue(search)
    if (search.length >= 2) {
      debouncedSearchRef.current?.(search)
    }
    // НЕ очищаем options — поиск сам обновит их
  }, [])

  const loadSelectedValue = useCallback(
    async (selectedValue: any) => {
      if (!apiUrl || !field) return
      if (selectedValue === null || selectedValue === undefined) return
      try {
        const url = `${apiUrl}?field=${field}&selected=${encodeURIComponent(selectedValue)}`
        const response = await fetchAPI(url)
        if (response?.selectedOption) {
          const selectedOption = response.selectedOption as IEnumOption
          setDisplayValue({
            value: selectedOption.value,
            label: selectedOption.label || selectedOption.title || String(selectedOption.value),
          })
        } else {
          // Если API не вернул выбранную опцию, показываем value как строку.
          setDisplayValue({ value: selectedValue, label: String(selectedValue) })
        }
      } catch (error) {
        console.error('Error loading selected value:', error)
        setDisplayValue({ value: selectedValue, label: String(selectedValue) })
      }
    },
    [apiUrl, field],
  )

  useEffect(() => {
    setLocalValue(value)

    if (value == null) {
      setDisplayValue(null)
      return
    }

    if (apiUrl && field) {
      loadSelectedValue(value)
      return
    }
  }, [apiUrl, field, loadSelectedValue, value])

  useEffect(() => {
    // fallback без API: пробуем найти label среди текущих options (например, при SSR/кешировании)
    if (value == null) return
    if (apiUrl && field) return

    const found = options.find(opt => String(opt.value) === String(value))
    if (found) {
      setDisplayValue({ value: found.value, label: found.label })
    } else {
      setDisplayValue({ value, label: String(value) })
    }
  }, [apiUrl, field, options, value])

  // При размонтировании отменяем запросы
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort()
      debouncedSearchRef.current?.cancel()
    }
  }, [])

  return (
    <SelectFromEnumAsyncStyles>
      <Select
        labelInValue
        value={displayValue}
        onChange={selectedObj => {
          const newVal = selectedObj ? selectedObj.value : null
          if (newVal !== localValue) {
            setLocalValue(newVal)
            onChange?.(newVal)
          }
          setDisplayValue(selectedObj)
        }}
        showSearch
        placeholder={placeholder}
        loading={loading}
        disabled={disabled}
        allowClear={allowClear}
        filterOption={false}
        onSearch={handleSearch}
        optionFilterProp={'optionFilterProp'}
        optionLabelProp={'optionLabelProp'}
        notFoundContent={
          loading ? (
            <Space style={{ width: '100%', justifyContent: 'center', padding: '10px 0' }}>
              <Spin size="small" />
            </Space>
          ) : searchValue.length < 2 ? (
            'Введите минимум 2 символа для поиска'
          ) : (
            'Ничего не найдено'
          )
        }
        style={{ width: '100%' }}
      >
        {options
          .filter(option => {
            if (localValue === null || localValue === undefined) return true
            return String(option.value) !== String(localValue)
          })
          .map(option => (
            <Option
              key={String(option.value)}
              value={option.value}
              title={option.title || option.label}
            >
              {option.label}
            </Option>
          ))}
      </Select>
    </SelectFromEnumAsyncStyles>
  )
}

export default SelectFromEnumAsync
